import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import "../../styles/AssignExerciseModal.css";
import { TrainerContext } from "../../context/TrainerContext";
import { IoSearchOutline } from 'react-icons/io5';
import { useToast } from "../commons/toast/ToastContext";

const AssignExerciseModal = ({ isOpen, onClose, onSave, dogId }) => {
    const { t } = useTranslation(['exercises', 'common']);
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const [exercises, setExercises] = useState([]);
    const [filteredExercises, setFilteredExercises] = useState([]);
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!isOpen) {
            setSelectedExercises([]);
            setSearchTerm("");
            return;
        }

        if (!trainer?.schoolId) return;

        setLoading(true);
        setExercises([]);
        setFilteredExercises([]);
        setError("");

        const fetchExercises = async () => {
            try {
                const token = localStorage.getItem("jwt");
                const response = await axios.get(`/api/v1/exercise/school/${trainer.schoolId}/dog/${dogId}/unassigned`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const exercisesData = response.data || [];
                setExercises(exercisesData);
                setFilteredExercises(exercisesData);
                setError("");
            } catch {
                setError(t('exercises:fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchExercises();
    }, [trainer, dogId, isOpen]);

    useEffect(() => {
        const filtered = exercises.filter((exercise) =>
            exercise.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredExercises(filtered);
    }, [searchTerm, exercises]);

    const handleSelectExercise = (exerciseId) => {
        setSelectedExercises((prevSelected) =>
            prevSelected.includes(exerciseId)
                ? prevSelected.filter((id) => id !== exerciseId)
                : [...prevSelected, exerciseId]
        );
    };

    const handleSave = async () => {
        if (selectedExercises.length === 0) {
            setError(t('exercises:selectAtLeastOne'));
            toast.warning(t('exercises:selectAtLeastOne'), t('exercises:noSelection'));
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const requests = selectedExercises.map((exerciseId) => ({
                exerciseId,
                assignedDate: new Date().toISOString().split("T")[0],
            }));

            await axios.post(`/api/v1/exercise/dog/${dogId}/assign`, requests, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setSelectedExercises([]);
            setSearchTerm("");
            setError("");
            toast.success(
                selectedExercises.length === 1
                    ? t('exercises:assignSuccessSingle')
                    : t('exercises:assignSuccessMultiple', { count: selectedExercises.length }),
                t('common:success')
            );
            onSave();
            onClose();
        } catch {
            setError(t('exercises:assignError'));
            toast.error(t('exercises:assignError'), t('common:error'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="assign-exercise-modal__overlay">
            <div className="assign-exercise-modal__content">
                <div className="assign-exercise-modal__header">
                    <div className="assign-exercise-modal__header-info">
                        <h2 className="assign-exercise-modal__title">{t('exercises:assignExercise')}</h2>
                        <div className="assign-exercise-modal__selected-count">
                            {selectedExercises.length > 0 && (
                                <span className="assign-exercise-modal__selected-badge">
                                    {t('exercises:selected')}: {selectedExercises.length}
                                </span>
                            )}
                        </div>
                    </div>
                    <button className="assign-exercise-modal__close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="assign-exercise-modal__body">
                    {/* Search Section with Icon */}
                    <div className="assign-exercise-modal__search-section">
                        <IoSearchOutline className="assign-exercise-modal__search-icon" />
                        <input
                            type="text"
                            className="assign-exercise-modal__search-bar"
                            placeholder={t('exercises:searchExercisePlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Error Message */}
                    {error && <div className="assign-exercise-modal__error">{error}</div>}

                    {/* Exercises Section */}
                    <div className="assign-exercise-modal__exercises-section">
                        <div className="assign-exercise-modal__exercises-header">
                            <span className="assign-exercise-modal__exercises-count">
                                {t('exercises:availableExercises', { count: filteredExercises.length })}
                            </span>
                        </div>

                        {loading ? (
                            <div className="assign-exercise-modal__loading">
                                {t('exercises:loadingExercises')}
                            </div>
                        ) : (
                            <div className="assign-exercise-modal__exercises-list">
                                {filteredExercises.length === 0 ? (
                                    <div className="assign-exercise-modal__no-exercises">
                                        {searchTerm ? (
                                            <>
                                                <div className="assign-exercise-modal__no-exercises-icon">🔍</div>
                                                <h3>{t('exercises:noResults')}</h3>
                                                <p>{t('exercises:noResultsFor', { search: searchTerm })}</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="assign-exercise-modal__no-exercises-icon">📋</div>
                                                <h3>{t('exercises:noAvailableExercises')}</h3>
                                                <p>{t('exercises:allExercisesAssigned')}</p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    filteredExercises.map((exercise) => (
                                        <label
                                            key={exercise.exerciseId}
                                            className={`assign-exercise-modal__exercise-option ${
                                                selectedExercises.includes(exercise.exerciseId)
                                                    ? 'assign-exercise-modal__exercise-option--selected'
                                                    : ''
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedExercises.includes(exercise.exerciseId)}
                                                onChange={() => handleSelectExercise(exercise.exerciseId)}
                                                className="assign-exercise-modal__exercise-checkbox"
                                            />
                                            <div className="assign-exercise-modal__exercise-info">
                                                <div className="assign-exercise-modal__exercise-name">
                                                    {exercise.name}
                                                </div>
                                                <div className="assign-exercise-modal__exercise-difficulty">
                                                    {t('exercises:level')}: {t(`exercises:difficulty.${(exercise.difficultyLevel || 'basic').toLowerCase()}`, { defaultValue: exercise.difficultyLevel })}
                                                </div>
                                            </div>
                                        </label>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="assign-exercise-modal__footer">
                    <button className="assign-exercise-modal__cancel-btn" onClick={onClose}>
                        {t('common:cancel')}
                    </button>
                    <button
                        className="assign-exercise-modal__save-btn"
                        onClick={handleSave}
                        disabled={selectedExercises.length === 0 || loading}
                    >
                        {t('exercises:assign')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignExerciseModal;